import { Maximize2, Minimize2, MoreVertical, SlidersHorizontal } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Menu } from '../components/Menu';
import {
  BusinessFilterChips,
  BusinessFilterPanel,
} from '../components/pipelines/BusinessFilterPanel';
import { BusinessBulkDialog } from '../components/pipelines/BusinessBulkDialog';
import { BusinessListView } from '../components/pipelines/BusinessListView';
import {
  EditPipelineDialog,
  type EditPipelineTab,
} from '../components/pipelines/EditPipelineDialog';
import { KanbanBoard, type BoardToast } from '../components/pipelines/KanbanBoard';
import { NewPipelineModal } from '../components/pipelines/PipelineModals';
import { PipelinePanelView } from '../components/pipelines/PipelinePanelView';
import { PipelineSidebar } from '../components/pipelines/PipelineSidebar';
import { setUiPrefs, useUiPrefs } from '../lib/ui-prefs';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorNote,
  Loading,
  compactInputClass,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth-context-value';
import {
  BUSINESS_SORT_LABELS,
  activeFilterCount,
  apiFilter,
  panelFilter,
  dayBoundary,
  isoToDay,
  type BusinessSort,
  type BusinessView,
} from '../lib/business-filter-model';
import { formatMoney } from '../lib/format';
import {
  businessSummary,
  listPipelines,
  listStages,
  pipelinesApi,
  type BusinessAction,
  type BusinessTarget,
} from '../lib/resources';

const VIEW_LABELS: Record<BusinessView, string> = {
  kanban: 'Kanban',
  lista: 'Lista',
  painel: 'Painel',
};

interface BulkState {
  action: BusinessAction;
  target: BusinessTarget;
  scopeLabel: string;
  defaultStageId?: string;
}

/**
 * Pipelines (doc 03): barra lateral com grupos e ☆, cabeçalho com Kanban |
 * Lista, busca, filtros, ordenação, intervalo e o menu ⋮ — tudo na URL, para o
 * link abrir a mesma visão e as ações em lote usarem o mesmo filtro.
 */
export function KanbanPage() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const uiPrefs = useUiPrefs();
  const [params, setParams] = useSearchParams();
  const [searchDraft, setSearchDraft] = useState(params.get('search') ?? '');
  const [showFilters, setShowFilters] = useState(false);
  const [newPipeline, setNewPipeline] = useState(false);
  const [editTab, setEditTab] = useState<EditPipelineTab | null>(null);
  const [bulk, setBulk] = useState<BulkState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState<BoardToast | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), toast.tone === 'error' ? 6000 : 3500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const pipelinesQuery = useQuery({ queryKey: ['pipelines'], queryFn: listPipelines });
  const pipelines = useMemo(() => pipelinesQuery.data?.data ?? [], [pipelinesQuery.data]);
  const requested = params.get('p');
  // Derivado: sem escolha na URL, abre a primeira favorita (ou a primeira).
  const pipelineId =
    (requested && pipelines.some((pipeline) => pipeline.id === requested) ? requested : null) ??
    pipelines.find((pipeline) => pipeline.isFavorite)?.id ??
    pipelines[0]?.id ??
    null;
  const requestedView = params.get('view');
  const view: BusinessView =
    requestedView === 'lista' || requestedView === 'painel' ? requestedView : 'kanban';
  const filter = apiFilter(params);

  const pipelineQuery = useQuery({
    queryKey: ['pipeline', pipelineId],
    queryFn: () => pipelinesApi.get(pipelineId!),
    enabled: Boolean(pipelineId),
  });
  const stagesQuery = useQuery({
    queryKey: ['stages', pipelineId],
    queryFn: () => listStages(pipelineId!),
    enabled: Boolean(pipelineId),
  });
  const summaryQuery = useQuery({
    queryKey: ['kanban', 'summary', pipelineId, filter],
    queryFn: () => businessSummary(pipelineId!, filter),
    enabled: Boolean(pipelineId),
  });

  const pipeline = pipelineQuery.data;
  const stages = stagesQuery.data ?? [];
  const summary = summaryQuery.data ?? [];

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['kanban'] });
    queryClient.invalidateQueries({ queryKey: ['stages', pipelineId] });
    queryClient.invalidateQueries({ queryKey: ['pipeline', pipelineId] });
    queryClient.invalidateQueries({ queryKey: ['pipelines'] });
    queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  };

  const duplicate = useMutation({
    mutationFn: () => pipelinesApi.duplicate(pipelineId!),
    onSuccess: (copy) => {
      refresh();
      update({ p: copy.id });
      setToast({ tone: 'info', text: `Pipeline duplicada: ${copy.name}` });
    },
    onError: (error) =>
      setToast({ tone: 'error', text: errorMessage(error, 'Falha ao duplicar') }),
  });
  const remove = useMutation({
    mutationFn: () => pipelinesApi.remove(pipelineId!),
    onSuccess: () => {
      setConfirmDelete(false);
      update({ p: null });
      refresh();
    },
  });

  if (pipelinesQuery.isLoading) return <Loading label="Carregando pipelines…" />;
  if (pipelinesQuery.isError) {
    return (
      <div className="p-6">
        <ErrorNote>
          {errorMessage(pipelinesQuery.error, 'Falha ao carregar pipelines')}
        </ErrorNote>
      </div>
    );
  }

  const focused = uiPrefs.navCollapsed && uiPrefs.pipelinesCollapsed;
  const canCreatePipeline = can('pipeline_admin');
  const filterCount = activeFilterCount(params);
  const pipelineTotal = summary.reduce((sum, item) => sum + item.total, 0);
  const pipelineCount = summary.reduce((sum, item) => sum + item.businessesCount, 0);
  const openBulk = (
    action: BusinessAction,
    target: BusinessTarget,
    scopeLabel: string,
    defaultStageId?: string,
  ) => setBulk({ action, target, scopeLabel, defaultStageId });

  return (
    <div className="flex h-full">
      <PipelineSidebar
        pipelines={pipelines}
        activeId={pipelineId}
        canManage={canCreatePipeline}
        onSelect={(id) => update({ p: id, stage: null })}
        onCreate={() => setNewPipeline(true)}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col p-6">
        {!pipelineId ? (
          <div className="space-y-4">
            <EmptyState
              title="Nenhuma pipeline para você"
              hint={
                canCreatePipeline
                  ? 'Crie a primeira pipeline pela barra lateral.'
                  : 'Peça a um administrador para incluir você numa pipeline.'
              }
            />
          </div>
        ) : !pipeline ? (
          pipelineQuery.isError ? (
            <ErrorNote>
              {errorMessage(pipelineQuery.error, 'Pipeline não encontrada')}
            </ErrorNote>
          ) : (
            <Loading label="Carregando pipeline…" />
          )
        ) : (
          <>
            <header className="mb-3 flex flex-wrap items-center gap-3">
              <div className="mr-auto min-w-0">
                <p className="text-xs text-slate-400">{pipeline.group ?? 'Pipelines'}</p>
                <h1 className="truncate text-xl font-semibold text-slate-900">
                  {pipeline.name}
                </h1>
                <p className="text-xs text-slate-500">
                  {pipelineCount} negócio(s) · {formatMoney(pipelineTotal)}
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setUiPrefs(
                    focused
                      ? { navCollapsed: false, pipelinesCollapsed: false }
                      : { navCollapsed: true, pipelinesCollapsed: true },
                  )
                }
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[13px] text-slate-600 hover:text-slate-900"
                title={
                  focused ? 'Mostrar o menu e as pipelines' : 'Recolher o menu e as pipelines'
                }
              >
                {focused ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                {focused ? 'Mostrar menus' : 'Só o quadro'}
              </button>

              <div className="flex rounded-lg border border-slate-300 bg-white p-0.5 text-sm">
                {(['kanban', 'lista', 'painel'] as BusinessView[]).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => update({ view: option === 'kanban' ? null : option })}
                    className={`rounded-md px-3 py-1 ${
                      view === option ? 'bg-slate-900 text-white' : 'text-slate-600'
                    }`}
                  >
                    {VIEW_LABELS[option]}
                  </button>
                ))}
              </div>

              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  update({ search: searchDraft.trim() || null });
                }}
              >
                <input
                  className={`${compactInputClass} w-56`}
                  placeholder="Buscar lead, e-mail ou #número"
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                  onBlur={() => update({ search: searchDraft.trim() || null })}
                />
              </form>

              <Button onClick={() => setShowFilters(true)}>
                <SlidersHorizontal size={14} /> Filtros
                {filterCount > 0 ? ` (${filterCount})` : ''}
              </Button>

              {view !== 'painel' && (
                <select
                  aria-label="Ordenação"
                  className={`${compactInputClass} w-48`}
                  value={params.get('sort') ?? 'recent'}
                  onChange={(event) =>
                    update({
                      sort: event.target.value === 'recent' ? null : event.target.value,
                    })
                  }
                >
                  {(Object.keys(BUSINESS_SORT_LABELS) as BusinessSort[]).map((sort) => (
                    <option key={sort} value={sort}>
                      {BUSINESS_SORT_LABELS[sort]}
                    </option>
                  ))}
                </select>
              )}

              <div
                className="flex items-center gap-1 text-xs text-slate-500"
                title="Intervalo de criação"
              >
                <input
                  type="date"
                  aria-label="Criados desde"
                  className={`${compactInputClass} w-36`}
                  value={isoToDay(params.get('createdAtFrom'))}
                  onChange={(event) =>
                    update({ createdAtFrom: dayBoundary(event.target.value, false) })
                  }
                />
                <span>até</span>
                <input
                  type="date"
                  aria-label="Criados até"
                  className={`${compactInputClass} w-36`}
                  value={isoToDay(params.get('createdAtTo'))}
                  onChange={(event) =>
                    update({ createdAtTo: dayBoundary(event.target.value, true) })
                  }
                />
              </div>

              <Menu
                trigger={(toggle) => (
                  <Button onClick={toggle} aria-label="Mais ações da pipeline">
                    <MoreVertical size={15} />
                  </Button>
                )}
                groups={[
                  {
                    title: 'Pipeline',
                    items: [
                      {
                        label: 'Editar pipeline',
                        onSelect: () => setEditTab('config'),
                        disabled: !pipeline.canManage,
                      },
                      {
                        label: 'Permissões (atendentes)',
                        onSelect: () => setEditTab('atendentes'),
                        disabled: !pipeline.canManage,
                      },
                      {
                        label: 'Atividades por etapa',
                        onSelect: () => setEditTab('atividades'),
                        disabled: !pipeline.canManage,
                      },
                      {
                        label: 'Condicionais das etapas',
                        onSelect: () => setEditTab('condicionais'),
                        disabled: !pipeline.canManage,
                      },
                      {
                        label: 'Duplicar pipeline',
                        onSelect: () => duplicate.mutate(),
                        disabled: !pipeline.canManage,
                      },
                      {
                        label: 'Excluir pipeline',
                        onSelect: () => setConfirmDelete(true),
                        disabled: !pipeline.canManage,
                        danger: true,
                      },
                    ],
                  },
                  {
                    title: 'Com os negócios filtrados',
                    items: (
                      [
                        ['move', 'Mover…'],
                        ['win', 'Ganhar…'],
                        ['lose', 'Perder…'],
                        ['restore', 'Restaurar status…'],
                        ['delete', 'Excluir…'],
                      ] as Array<[BusinessAction, string]>
                    ).map(([action, label]) => ({
                      label,
                      danger: action === 'delete',
                      disabled: action === 'delete' && pipeline.myFlags.disableDelete,
                      onSelect: () =>
                        openBulk(
                          action,
                          { pipelineId: pipeline.id, filter },
                          'do filtro atual',
                        ),
                    })),
                  },
                ]}
              />
            </header>

            <div className="mb-3">
              <BusinessFilterChips
                params={params}
                onRemove={(key) => update({ [key]: null })}
              />
            </div>

            {view === 'painel' ? (
              <PipelinePanelView pipelineId={pipeline.id} filter={panelFilter(params)} />
            ) : view === 'kanban' ? (
              <KanbanBoard
                key={pipeline.id}
                pipeline={pipeline}
                stages={stages}
                summary={summary}
                filter={filter}
                onChanged={refresh}
                onToast={setToast}
                onBulk={(action, stage) =>
                  openBulk(
                    action,
                    { pipelineId: pipeline.id, stageId: stage.id, filter },
                    `da etapa "${stage.name}"`,
                    stages.find((item) => item.id !== stage.id)?.id,
                  )
                }
              />
            ) : (
              <BusinessListView
                stages={stages}
                summary={summary}
                filter={filter}
                stageId={params.get('stage')}
                onStage={(stageId) => update({ stage: stageId })}
                onBulk={(action, ids) => openBulk(action, { ids }, 'selecionados')}
              />
            )}
          </>
        )}
      </div>

      {toast && (
        <div
          role="status"
          className={`fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl px-4 py-3 text-sm shadow-xl ${
            toast.tone === 'error' ? 'bg-red-600 text-white' : 'bg-slate-900 text-white'
          }`}
        >
          {toast.text}
        </div>
      )}

      {showFilters && (
        <BusinessFilterPanel
          params={params}
          onApply={(next) => update(next)}
          onClose={() => setShowFilters(false)}
        />
      )}
      {newPipeline && (
        <NewPipelineModal
          onClose={() => setNewPipeline(false)}
          onCreated={(created) => {
            refresh();
            update({ p: created.id });
          }}
        />
      )}
      {editTab && pipeline && (
        <EditPipelineDialog
          pipeline={pipeline}
          stages={stages}
          initialTab={editTab}
          onClose={() => setEditTab(null)}
          onChanged={refresh}
        />
      )}
      {bulk && (
        <BusinessBulkDialog
          action={bulk.action}
          target={bulk.target}
          scopeLabel={bulk.scopeLabel}
          stages={stages}
          defaultStageId={bulk.defaultStageId}
          onClose={() => setBulk(null)}
          onDone={refresh}
        />
      )}
      {confirmDelete && pipeline && (
        <ConfirmDialog
          title="Excluir pipeline"
          message={
            <>
              A pipeline <strong>{pipeline.name}</strong> vai para a Lixeira com as{' '}
              {stages.length} etapa(s) e todos os negócios dela. Dá para restaurar tudo em até
              30 dias.
            </>
          }
          confirmLabel="Excluir pipeline"
          danger
          busy={remove.isPending}
          error={remove.error ? errorMessage(remove.error, 'Falha ao excluir') : null}
          onConfirm={() => remove.mutate()}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
